import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAIL_AUTH_READY_EVENT } from "@/auth/constants";
import { isMailSessionVerified } from "@/auth/session";
import { cancelMailSnooze, setMailSnooze } from "@/lib/api/mailSnooze";
import { clearMutationTombstoneUids } from "@/lib/mail/mailMutationHold";
import { refreshFolderFromImap } from "@/lib/mail/folderWarmup";
import {
  fetchMailSnoozesCoordinated,
  invalidateSnoozeFetchCache,
} from "@/lib/snooze/snoozeFetchCoordinator";
import { getSnoozeCache, setSnoozeCache } from "@/lib/snooze/snoozeSessionCache";
import {
  snoozeEntryKey,
  type MailSnoozeEntry,
  type MailSnoozeWoke,
} from "@/lib/snooze/constants";
import { notifySnoozeWoke } from "@/lib/snooze/mailSnoozeWakeEvents";
import { setActiveSnoozeHiddenUids } from "@/lib/snooze/mailSnoozeActiveHide";
import { markWokeMessagesUnread } from "@/lib/snooze/snoozeWake";
import {
  completeMailActivity,
  failMailActivity,
  mailActivitySubjectLabel,
  startMailActivity,
} from "@/lib/mail/mailActivityJournalNote";
import type { MailMessageSummary } from "@/lib/types/mail";

/** Filet si l’onglet est throttlé / cron en retard. */
const SNOOZE_DUE_POLL_MS = 30_000;
/** Léger slack après l’heure pile. */
const SNOOZE_WAKE_SLACK_MS = 250;
const RECENT_WAKE_TTL_MS = 120_000;

export { clearSnoozeRuntimeCache, clearSnoozeRuntimeCache as clearMailSnoozeRuntimeCache } from "@/lib/snooze/snoozeSessionCache";

function entryToSummary(entry: MailSnoozeEntry): MailMessageSummary {
  return {
    uid: entry.uid,
    subject: entry.subject?.trim() || "(Sans objet)",
    from: "En attente",
    date: entry.until,
    seen: true,
    flagged: false,
    size: 0,
    snoozeUntil: entry.until,
    imapFolder: entry.holdFolder?.trim() || entry.folder,
    imapBodyUid: entry.holdUid ?? entry.uid,
  };
}

function entryToWoke(entry: MailSnoozeEntry): MailSnoozeWoke {
  return {
    uid: entry.uid,
    folder: entry.folder,
    holdFolder: entry.holdFolder,
    holdUid: entry.holdUid,
    subject: entry.subject ?? null,
  };
}

function isSnoozeDue(entry: MailSnoozeEntry, now = Date.now()): boolean {
  const ts = new Date(entry.until).getTime();
  if (!Number.isFinite(ts)) return false;
  return ts <= now;
}

function nextSnoozeWakeMs(entries: MailSnoozeEntry[]): number | null {
  const now = Date.now();
  let minFuture: number | null = null;
  let hasDue = false;
  for (const entry of entries) {
    const t = new Date(entry.until).getTime();
    if (!Number.isFinite(t)) continue;
    if (t <= now) {
      hasDue = true;
      continue;
    }
    if (minFuture == null || t < minFuture) minFuture = t;
  }
  if (hasDue) return now;
  return minFuture;
}

export function useMailSnooze(accountId?: number, options?: { enabled?: boolean }) {
  const enabled = options?.enabled !== false;
  const [snoozes, setSnoozes] = useState<MailSnoozeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const reloadRef = useRef<(options?: { silent?: boolean }) => Promise<MailSnoozeWoke[]>>(
    async () => []
  );
  const onWakeRef = useRef<((woke: MailSnoozeWoke[]) => void) | null>(null);
  const recentWakeRef = useRef<Map<string, number>>(new Map());
  const tickInFlightRef = useRef(false);
  const snoozesRef = useRef<MailSnoozeEntry[]>([]);
  snoozesRef.current = snoozes;

  useEffect(() => {
    if (accountId == null) return;
    setActiveSnoozeHiddenUids(accountId, snoozes);
  }, [accountId, snoozes]);

  const emitWake = useCallback((woke: MailSnoozeWoke[]) => {
    if (woke.length === 0 || accountId == null) return;

    const now = Date.now();
    const recent = recentWakeRef.current;
    for (const [key, ts] of recent) {
      if (now - ts > RECENT_WAKE_TTL_MS) recent.delete(key);
    }

    const fresh = woke.filter((item) => {
      if (item.uid < 1 || !item.folder) return false;
      const key = snoozeEntryKey(item.folder, item.uid);
      if (recent.has(key)) return false;
      recent.set(key, now);
      return true;
    });

    if (fresh.length === 0) return;

    // Gmail local (et filet général) : lever tombstone pour que le mail réapparaisse en INBOX.
    for (const item of fresh) {
      if (item.uid > 0 && item.folder) {
        clearMutationTombstoneUids(accountId, item.folder, [item.uid]);
      }
    }

    notifySnoozeWoke(
      accountId,
      fresh.map((item) => ({
        folder: item.folder,
        uid: item.uid,
        subject: item.subject,
        holdFolder: item.holdFolder,
      }))
    );
    onWakeRef.current?.(fresh);
    void markWokeMessagesUnread(accountId, fresh);
  }, [accountId]);

  const purgeDueLocally = useCallback(
    (due: MailSnoozeEntry[]) => {
      if (accountId == null || due.length === 0) return;
      const dueKeys = new Set(due.map((entry) => snoozeEntryKey(entry.folder, entry.uid)));
      setSnoozes((prev) => {
        const next = prev.filter((entry) => !dueKeys.has(snoozeEntryKey(entry.folder, entry.uid)));
        setSnoozeCache(accountId, next);
        return next;
      });
    },
    [accountId]
  );

  const reload = useCallback(
    async (options?: { silent?: boolean }): Promise<MailSnoozeWoke[]> => {
      if (accountId == null) {
        setSnoozes([]);
        return [];
      }

      const cached = getSnoozeCache(accountId);
      if (cached?.length) {
        setSnoozes(cached);
      }

      if (!options?.silent) {
        setLoading(true);
      }

      try {
        const res = await fetchMailSnoozesCoordinated({
          accountId,
          force: !options?.silent,
        });
        if (!res.ok) return [];
        const next = res.data.snoozes ?? [];
        setSnoozeCache(accountId, next);
        setSnoozes(next);
        const woke = res.data.woke ?? [];
        if (woke.length > 0) {
          emitWake(woke);
        }
        return woke;
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [accountId, emitWake]
  );

  reloadRef.current = reload;

  useEffect(() => {
    if (!enabled || accountId == null) {
      setSnoozes([]);
      return;
    }

    const cached = getSnoozeCache(accountId);
    if (cached) setSnoozes(cached);

    let cancelled = false;
    const boot = () => {
      if (!cancelled) void reload({ silent: Boolean(cached?.length) });
    };

    if (isMailSessionVerified() || window.__mailSessionVerified === true) {
      boot();
      return () => {
        cancelled = true;
      };
    }

    window.addEventListener(MAIL_AUTH_READY_EVENT, boot, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener(MAIL_AUTH_READY_EVENT, boot);
    };
  }, [accountId, enabled, reload]);

  const hasPending = snoozes.length > 0;
  const nextWakeAt = useMemo(() => nextSnoozeWakeMs(snoozes), [snoozes]);

  /** Timer à l’heure pile + poll filet (réveil MySQL + non lu IMAP côté client). */
  useEffect(() => {
    if (!enabled || accountId == null) return;
    if (!hasPending && nextWakeAt == null) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled || tickInFlightRef.current) return;
      const due = snoozesRef.current.filter((entry) => isSnoozeDue(entry));
      if (due.length > 0) {
        purgeDueLocally(due);
        emitWake(due.map(entryToWoke));
      }
      tickInFlightRef.current = true;
      try {
        invalidateSnoozeFetchCache(accountId);
        // force: true pour obtenir woke[] frais (pas le cache 5 s du coordinator).
        const res = await fetchMailSnoozesCoordinated({ accountId, force: true });
        if (!cancelled && res.ok) {
          const next = res.data.snoozes ?? [];
          setSnoozeCache(accountId, next);
          setSnoozes(next);
          const woke = res.data.woke ?? [];
          if (woke.length > 0) {
            emitWake(woke);
          }
        }
      } finally {
        tickInFlightRef.current = false;
      }
    };

    void tick();

    const intervalId = window.setInterval(() => {
      void tick();
    }, SNOOZE_DUE_POLL_MS);

    let wakeTimerId: number | undefined;
    if (nextWakeAt != null) {
      const delay = Math.max(SNOOZE_WAKE_SLACK_MS, nextWakeAt - Date.now() + SNOOZE_WAKE_SLACK_MS);
      wakeTimerId = window.setTimeout(() => {
        void tick();
      }, delay);
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (wakeTimerId != null) window.clearTimeout(wakeTimerId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accountId, emitWake, enabled, hasPending, nextWakeAt, purgeDueLocally]);

  const snoozeMap = useMemo(() => {
    const map = new Map<string, MailSnoozeEntry>();
    for (const entry of snoozes) {
      if (entry.uid > 0) {
        map.set(snoozeEntryKey(entry.folder, entry.uid), entry);
      }
    }
    return map;
  }, [snoozes]);

  const isSnoozed = useCallback(
    (folder: string, uid: number) => snoozeMap.has(snoozeEntryKey(folder, uid)),
    [snoozeMap]
  );

  const snooze = useCallback(
    async (
      folder: string,
      uid: number,
      until: string,
      subject?: string,
      options?: { holdFolder?: string | null }
    ): Promise<{ ok: boolean; message?: string }> => {
      if (accountId == null || uid < 1) {
        return { ok: false, message: "Message invalide." };
      }

      const holdFolder = options?.holdFolder?.trim() || null;
      const optimistic: MailSnoozeEntry = {
        uid,
        folder,
        holdFolder,
        until,
        subject: subject?.trim() || null,
      };

      setSnoozes((prev) => {
        const filtered = prev.filter(
          (e) => snoozeEntryKey(e.folder, e.uid) !== snoozeEntryKey(folder, uid)
        );
        const next = [...filtered, optimistic].sort(
          (a, b) => new Date(a.until).getTime() - new Date(b.until).getTime()
        );
        setSnoozeCache(accountId, next);
        return next;
      });

      const journalId = startMailActivity(
        mailActivitySubjectLabel("Mise en attente", subject),
        "other"
      );

      const res = await setMailSnooze({
        accountId,
        folder,
        uid,
        until,
        subject,
        holdFolder,
      });
      if (!res.ok) {
        failMailActivity(journalId, res.message);
        invalidateSnoozeFetchCache(accountId);
        void reload({ silent: true });
        return { ok: false, message: res.message };
      }
      if (res.data.snooze) {
        setSnoozes((prev) => {
          const next = prev.map((e) =>
            snoozeEntryKey(e.folder, e.uid) === snoozeEntryKey(folder, uid)
              ? res.data.snooze
              : e
          );
          setSnoozeCache(accountId, next);
          return next;
        });
      }
      completeMailActivity(
        journalId,
        mailActivitySubjectLabel("En attente", subject)
      );
      return { ok: true };
    },
    [accountId, reload]
  );

  const unsnooze = useCallback(
    async (folder: string, uid: number) => {
      if (accountId == null || uid < 1) return false;

      clearMutationTombstoneUids(accountId, folder, [uid]);

      setSnoozes((prev) => {
        const next = prev.filter(
          (e) => snoozeEntryKey(e.folder, e.uid) !== snoozeEntryKey(folder, uid)
        );
        setSnoozeCache(accountId, next);
        return next;
      });

      const journalId = startMailActivity("Fin de mise en attente", "other");

      const res = await cancelMailSnooze({ accountId, folder, uid });
      if (!res.ok) {
        failMailActivity(journalId, res.message);
        invalidateSnoozeFetchCache(accountId);
        void reload({ silent: true });
        return false;
      }
      completeMailActivity(journalId, "Mise en attente annulée");
      void refreshFolderFromImap(accountId, folder);
      return true;
    },
    [accountId, reload]
  );

  const setOnWake = useCallback((handler: ((woke: MailSnoozeWoke[]) => void) | null) => {
    onWakeRef.current = handler;
  }, []);

  const snoozedMessages = useMemo(() => snoozes.map(entryToSummary), [snoozes]);

  return {
    snoozes,
    snoozeMap,
    snoozedMessages,
    snoozedCount: snoozes.length,
    loading,
    reload,
    isSnoozed,
    snooze,
    unsnooze,
    setOnWake,
  };
}
